import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { Button } from '@/components/ui/Button'
import { useLogin } from '@/features/auth/hooks/useAuth'
import { AuthLayout } from '@/layouts/AuthLayout'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'

const schema = z.object({
  email: z.email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

type FormValues = z.infer<typeof schema>

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const login = useLogin()
  const [apiError, setApiError] = useState<unknown>(null)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    login.mutate(values, {
      onSuccess: (result) => {
        if (result.requiresTwoFactor) {
          void navigate('/two-factor/challenge', {
            state: { twoFactorToken: result.twoFactorToken },
          })
          return
        }
        const redirectTo =
          (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/'
        void navigate(redirectTo, { replace: true })
      },
      onError: (error) => setApiError(error),
    })
  })

  return (
    <AuthLayout title="Sign In" subtitle="Welcome back to Job Portal">
      <FormErrorSummary
        errors={errors}
        serverErrors={getFieldErrors(apiError)}
        generalMessage={apiError ? getErrorMessage(apiError) : null}
      />
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <FormInput control={control} name="email" label="Email" type="email" required />
        <FormInput control={control} name="password" label="Password" type="password" required />
        <div className="d-flex justify-content-end mb-3">
          <Link to="/forgot-password" className="fs-12 text-primary">
            Forgot password?
          </Link>
        </div>
        <Button type="submit" className="w-100" loading={login.isPending}>
          Sign In
        </Button>
      </form>
      <div className="text-center mt-3">
        <p className="text-muted mb-1">
          New student?{' '}
          <Link to="/register/student" className="text-primary">
            Create an account
          </Link>
        </p>
        <p className="text-muted mb-0">
          Hiring?{' '}
          <Link to="/register/recruiter" className="text-primary">
            Register your company
          </Link>
        </p>
      </div>
    </AuthLayout>
  )
}
